<template>
  <div class="box-b">
    <p class="under">DIRECTED</p>
    <div v-if="component.props?.description" v-html="safeDescription" />

    <RadioButton
      v-model="choice"
      :input-id="`boxB-${component.id}`"
      value="b"
      :disabled="disabled"
    />
    <label :for="`boxB-${component.id}`" style="margin-left: 8px;">
      <strong>{{ component.props?.label || 'Box B' }}</strong>
    </label>

    <div v-show="choice === 'b'" class="candidate-grid">
      <div class="columns">
        <div class="column is-half">
          <div
            v-for="cand in candidates"
            :key="cand.id"
            class="candidate-row"
          >
            <label>{{ cand.name }}</label>
            <input
              :value="directed[cand.id]"
              type="number"
              min="0"
              step="0.0001"
              :disabled="disabled"
              @input="(e) => setDirected(cand.id, e.target.value)"
            />
          </div>
        </div>
        <div class="column is-half">
          <h3 class="subtitle is-4">Votes remaining:</h3>
          <p class="votes_remaining" :class="{ 'has-text-danger': remaining < 0 }">
            {{ remaining }}
          </p>
          <p v-if="remaining < 0" class="has-text-danger">
            You have used more votes than available.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import RadioButton from 'primevue/radiobutton'
import DOMPurify from 'dompurify'

const props = defineProps({
  component: { type: Object, required: true },
  shareholder: { type: Object, default: () => ({}) },
  mode: { type: String, default: 'vote' },
  form: { type: Object, default: null },
  shareValue: { type: Number, default: 2 },
  ballotComponents: { type: Array, default: () => [] },
})

const disabled = computed(() => props.mode !== 'vote')
const questionId = computed(() => props.component.props?.questionId)

const safeDescription = computed(() =>
  DOMPurify.sanitize(props.component.props?.description || ''),
)

const question = computed(() =>
  props.ballotComponents.find(
    (c) => c.type === 'ballotQuestion' && c.id === questionId.value,
  ),
)
const candidates = computed(() => question.value?.props?.candidates || [])

const choice = computed({
  get() {
    return props.form?.answers?.[questionId.value]?.choice || null
  },
  set(v) {
    if (!props.form || !questionId.value) return
    if (!props.form.answers[questionId.value]) {
      props.form.answers[questionId.value] = { choice: null, directed: {} }
    }
    props.form.answers[questionId.value].choice = v
  },
})

const directed = computed(() => {
  if (!props.form || !questionId.value) return {}
  return props.form.answers?.[questionId.value]?.directed || {}
})

function ensureAnswer() {
  if (!props.form || !questionId.value) return null
  if (!props.form.answers[questionId.value]) {
    props.form.answers[questionId.value] = { choice: null, directed: {} }
  }
  return props.form.answers[questionId.value]
}

function setDirected(candId, value) {
  const ans = ensureAnswer()
  if (!ans) return
  const num = parseFloat(value)
  ans.directed[candId] = Number.isFinite(num) ? num : 0
  if (ans.choice !== 'b') ans.choice = 'b'
}

const totalVotesAvailable = computed(() => {
  const shares = props.form?.signcustodian
    ? Number(props.form.custodianShares || 0)
    : Number(props.shareholder.shares || 0)
  return shares * props.shareValue
})

const directedSum = computed(() =>
  Object.values(directed.value).reduce(
    (acc, v) => acc + (parseFloat(v) || 0),
    0,
  ),
)

const remaining = computed(() => {
  return (
    Math.round((totalVotesAvailable.value - directedSum.value) * 10000) / 10000
  )
})
</script>

<style scoped>
.box-b {
  padding: 10px 0;
}

.under {
  text-decoration: underline;
  font-weight: 700;
  margin-bottom: 8px;
}

.candidate-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;

  label {
    flex: 1;
  }

  input[type='number'] {
    width: 120px;
    height: 40px;
    border-radius: 6px;
    border: 1.5px solid #bbb;
    padding: 6px 10px;
    font-size: 16px;

    &:focus {
      outline: none;
      border-color: #0a8df8;
    }
  }
}

.votes_remaining {
  color: #1679ca;
  font-size: 26px;
  font-weight: 700;
}

.candidate-grid {
  margin-top: 12px;
}
</style>
